import {
    convertSecondsPerMeterToSecondsPerKilometerOrMile
} from '@race-planner/unit-converters';
import { calculatePace } from './calculate-pace';
import { calculateRunTimeForPace } from './calculate-run-time-for-pace';
import { calculateRunDistanceForPace } from './calculate-run-distance-for-pace';

/**
 * Calculates combined pace for run walk schema
 */
const calculateRunWalkCombinedPace = ({
    runIntervalInSeconds,
    walkIntervalInSeconds,
    runPaceInSeconds,
    walkPaceInSeconds
}) => {
    const combinedPace = (runIntervalInSeconds + walkIntervalInSeconds) /
        (
            (runIntervalInSeconds / runPaceInSeconds) +
            (walkIntervalInSeconds / walkPaceInSeconds)
        );

    return (
        Number.isNaN(combinedPace) ||
        !Number.isFinite(combinedPace)
    ) ? 0 : combinedPace;
};

export const calculateRunWalkTotalTime = ({
    runIntervalInSeconds,
    walkIntervalInSeconds,
    runPaceInSeconds,
    walkPaceInSeconds,
    distanceInMeters,
    unitsOfLength
}) => {
    const runDistanceInMetersInInterval = calculateRunDistanceForPace({
        timeInSeconds: runIntervalInSeconds,
        paceInSeconds: runPaceInSeconds,
        unitsOfLength
    });
    /**
     * If run distance in one interval is equal or longer than total distance,
     * use run pace as the target pace, otherwise, use combined pace
     */
    const paceInSeconds = distanceInMeters > runDistanceInMetersInInterval ?
        calculateRunWalkCombinedPace({
            runIntervalInSeconds,
            walkIntervalInSeconds,
            runPaceInSeconds,
            walkPaceInSeconds
        }) :
        runPaceInSeconds;

    return calculateRunTimeForPace({
        paceInSeconds,
        distanceInMeters,
        unitsOfLength
    });
};

export const calculateRunWalkTotalDistance = ({
    runIntervalInSeconds,
    walkIntervalInSeconds,
    runPaceInSeconds,
    walkPaceInSeconds,
    timeInSeconds,
    unitsOfLength
}) => {
    /**
     * If run interval is equal or longer than total time,
     * use run pace as the target pace, otherwise, use combined pace
     */
    const paceInSeconds = timeInSeconds > runIntervalInSeconds ?
        calculateRunWalkCombinedPace({
            runIntervalInSeconds,
            walkIntervalInSeconds,
            runPaceInSeconds,
            walkPaceInSeconds
        }) :
        runPaceInSeconds;

    return calculateRunDistanceForPace({
        timeInSeconds,
        paceInSeconds,
        unitsOfLength
    });
};

export const calculateRunWalkWalkInterval = ({
    timeInSeconds,
    distanceInMeters,
    runIntervalInSeconds,
    runPaceInSeconds,
    walkPaceInSeconds,
    unitsOfLength
}) => {
    const combinedPacePerMeters = calculatePace({ timeInSeconds, distanceInMeters });
    const combinedPace = convertSecondsPerMeterToSecondsPerKilometerOrMile({
        paceInSecondsPerMeter: combinedPacePerMeters,
        unitsOfLength
    });
    const walkIntervalInSeconds =
        walkPaceInSeconds *
        (runIntervalInSeconds - combinedPace * runIntervalInSeconds / runPaceInSeconds) /
        (combinedPace - walkPaceInSeconds);

    return (
        Number.isNaN(walkIntervalInSeconds) ||
        !Number.isFinite(walkIntervalInSeconds)
    ) ? 0 : walkIntervalInSeconds;
};

export const calculateRunWalkWalkPace = ({
    timeInSeconds,
    distanceInMeters,
    runIntervalInSeconds,
    runPaceInSeconds,
    walkIntervalInSeconds,
    unitsOfLength
}) => {
    const combinedPacePerMeters = calculatePace({ timeInSeconds, distanceInMeters });
    const combinedPace = convertSecondsPerMeterToSecondsPerKilometerOrMile({
        paceInSecondsPerMeter: combinedPacePerMeters,
        unitsOfLength
    });
    const walkPaceInSeconds = walkIntervalInSeconds /
        (
            (runIntervalInSeconds + walkIntervalInSeconds) / combinedPace -
            runIntervalInSeconds / runPaceInSeconds
        );

    return (
        Number.isNaN(walkPaceInSeconds) ||
        !Number.isFinite(walkPaceInSeconds)
    ) ? 0 : walkPaceInSeconds;
};

export const calculateRunWalkRunInterval = ({
    timeInSeconds,
    distanceInMeters,
    runPaceInSeconds,
    walkIntervalInSeconds,
    walkPaceInSeconds,
    unitsOfLength
}) => {
    const combinedPacePerMeters = calculatePace({ timeInSeconds, distanceInMeters });
    const combinedPace = convertSecondsPerMeterToSecondsPerKilometerOrMile({
        paceInSecondsPerMeter: combinedPacePerMeters,
        unitsOfLength
    });
    const runIntervalInSeconds =
        runPaceInSeconds *
        (walkIntervalInSeconds - combinedPace * walkIntervalInSeconds / walkPaceInSeconds) /
        (combinedPace - runPaceInSeconds);

    return (
        Number.isNaN(runIntervalInSeconds) ||
        !Number.isFinite(runIntervalInSeconds)
    ) ? 0 : runIntervalInSeconds;
};

export const calculateRunWalkRunPace = ({
    timeInSeconds,
    distanceInMeters,
    runIntervalInSeconds,
    walkIntervalInSeconds,
    walkPaceInSeconds,
    unitsOfLength
}) => {
    const combinedPacePerMeters = calculatePace({ timeInSeconds, distanceInMeters });
    const combinedPace = convertSecondsPerMeterToSecondsPerKilometerOrMile({
        paceInSecondsPerMeter: combinedPacePerMeters,
        unitsOfLength
    });
    const runPaceInSeconds = runIntervalInSeconds /
        (
            (runIntervalInSeconds + walkIntervalInSeconds) / combinedPace -
            walkIntervalInSeconds / walkPaceInSeconds
        );

    return (
        Number.isNaN(runPaceInSeconds) ||
        !Number.isFinite(runPaceInSeconds)
    ) ? 0 : runPaceInSeconds;
};
